import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Confirms and performs account deactivation.
 *
 * The endpoint is still `POST /user/delete-account` (unchanged, so the backend
 * contract holds), but what it actually does is a soft deactivation: sessions
 * are revoked, sign-in is blocked, the subscription is scheduled for
 * cancellation, and journals plus account data are retained — signing in again
 * restores the account. The user-facing wording says exactly that rather than
 * implying permanent erasure.
 *
 * Uses AlertDialog rather than the plain Dialog the other account dialogs use:
 * it's disruptive enough that it shouldn't be dismissable by clicking outside
 * mid-confirm.
 */
export function DeleteAccountDialog({ open, onOpenChange }: Props) {
  const { logout } = useAuth();
  const [, setLocation] = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  async function handleConfirm() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await authFetch(apiUrl("/user/delete-account"), { method: "POST" });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Request failed (${res.status})`);
      }
      await logout();
      setLocation("/");
    } catch (err) {
      setError((err as Error).message ?? "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!submitting) onOpenChange(next);
      }}
    >
      <AlertDialogContent className="sm:max-w-[440px] rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">Deactivate your account?</AlertDialogTitle>
          <AlertDialogDescription className="leading-relaxed">
            You'll be signed out everywhere and won't be able to sign in until you reactivate. Your
            unfoldings and account data are kept — signing in again with this email restores your
            account. Any active subscription will be canceled.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && (
          <p className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-destructive px-5 text-sm font-medium text-white transition-colors hover:bg-destructive/90 disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Deactivating…
              </>
            ) : (
              "Deactivate account"
            )}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
